"use client";

/**
 * route-interactive.ts
 *
 * Event: `route.interactive`
 *
 * Expected daily volume: ~400/day (comparable to $pageview), upper bound
 * ~5000/day per env. Driver: one event per route navigation, bounded by
 * (user session count × navs per session).
 *
 * Kill switch: PostHog feature flag `posthog-page-load-events-enabled`
 * (boolean, default ON). Read via `posthog.isFeatureEnabled(...)`. If
 * the flag is `false` OR unevaluated (still loading), emit is skipped —
 * fail closed so we never emit while the flag state is unknown.
 *
 * Sampling: PostHog feature flag `route-interactive-sample-rates` (JSON
 * payload), per severity:
 *   { "ok": 1.0, "slow": 1.0, "very_slow": 1.0, "critical": 1.0 }
 * Only `ok` should ever be dialled below 1.0 — slow/very_slow/critical
 * must remain at 1.0 because alerts depend on deterministic emission.
 * The payload is read once at boot and cached in a module-level var;
 * flipping the rate at the PostHog UI applies on the user's next
 * flag-refresh interval (no redeploy).
 *
 * Cost-watch tile: https://us.posthog.com/project/157049/insights/kc95B5Y6
 *
 * If volume exceeds 5000/day, the per-navigation guard is broken —
 * investigate the dedup logic in RouteInteractiveTracker.
 *
 * Schema:
 *   route          string  — pathname snapshot at navigation start
 *   total_ms       number  — navigation start → primary data on screen
 *   client_ms      number  — total_ms minus server_ms; 0 if unknown
 *   server_ms      number|null — from layout_prefetch_duration when known
 *   data_source    "rxdb" | "server_action" | "cache" | "unknown"
 *   was_cold       boolean — first navigation in this session?
 *   was_capped     boolean — did the 30s hard cap fire?
 *   severity       "ok" | "slow" | "very_slow" | "critical"
 *                  bands: <3000 ok, <10000 slow, <25000 very_slow, >=25000 critical
 *   ready_signal   "incremental_render" | "loading_page" | "cap"
 */

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import posthog from "posthog-js";
import { captureProductClientEvent } from "@/lib/analytics/product-client-event";
import { ANALYTICS_EVENTS } from "./events";
import { clampDurationMs } from "./duration";

const KILL_SWITCH_FLAG = "posthog-page-load-events-enabled";
const SAMPLE_RATES_FLAG = "route-interactive-sample-rates";

/** Hard cap on how long we wait for a ready signal before emitting anyway. */
const READY_CAP_MS = 30_000;

export type RouteInteractiveSeverity = "ok" | "slow" | "very_slow" | "critical";
export type RouteInteractiveReadySignal =
  | "incremental_render"
  | "loading_page"
  | "cap";
export type RouteInteractiveDataSource =
  | "rxdb"
  | "server_action"
  | "cache"
  | "unknown";

interface SampleRates {
  ok: number;
  slow: number;
  very_slow: number;
  critical: number;
}

const DEFAULT_SAMPLE_RATES: SampleRates = {
  ok: 1.0,
  slow: 1.0,
  very_slow: 1.0,
  critical: 1.0,
};

let cachedSampleRates: SampleRates | null = null;

/**
 * Narrow an unknown payload value into a valid [0, 1] sample rate.
 * Anything else (NaN, negative, >1, non-number) falls back to the
 * caller's default. Treat the PostHog payload as untrusted — a
 * misconfigured flag must not break volume controls.
 */
function sanitizeRate(value: unknown, fallback: number): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return fallback;
  if (value < 0) return 0;
  if (value > 1) return 1;
  return value;
}

function readSampleRates(): SampleRates {
  if (cachedSampleRates) return cachedSampleRates;
  try {
    const payload = posthog.getFeatureFlagPayload(SAMPLE_RATES_FLAG);
    if (payload && typeof payload === "object" && !Array.isArray(payload)) {
      const p = payload as Record<string, unknown>;
      const rates: SampleRates = {
        ok: sanitizeRate(p.ok, DEFAULT_SAMPLE_RATES.ok),
        slow: sanitizeRate(p.slow, DEFAULT_SAMPLE_RATES.slow),
        very_slow: sanitizeRate(p.very_slow, DEFAULT_SAMPLE_RATES.very_slow),
        critical: sanitizeRate(p.critical, DEFAULT_SAMPLE_RATES.critical),
      };
      cachedSampleRates = rates;
      return rates;
    }
  } catch {
    // posthog not initialised yet — fall through to defaults.
  }
  return DEFAULT_SAMPLE_RATES;
}

/** Reset the cached sample-rates payload. Test-only. */
export function __resetRouteInteractiveForTests(): void {
  cachedSampleRates = null;
  tracker.reset();
}

/** Drive a navigation start without needing the React hook. Test-only. */
export function __startNavForTests(
  pathname: string,
  serverMs: number | null = null,
): void {
  tracker.start(pathname, serverMs);
}

function bandSeverity(totalMs: number): RouteInteractiveSeverity {
  if (totalMs < 3000) return "ok";
  if (totalMs < 10_000) return "slow";
  if (totalMs < 25_000) return "very_slow";
  return "critical";
}

/** The most recently started navigation, finalized or not. */
export interface RouteNavigation {
  pathname: string;
  /** `Date.now()` when the new route committed (not when the link was clicked). */
  startedAt: number;
  /** True for the document's first route, i.e. a hard load. */
  wasCold: boolean;
}

interface PendingNav {
  pathname: string;
  startedAt: number;
  serverMs: number | null;
  capTimer: ReturnType<typeof setTimeout> | null;
  /**
   * Captured at start() time, not finalize(). Survives supersession:
   * the first started nav consumes the "cold" flag even if it never
   * finalises — otherwise back-to-back fast navs both report was_cold.
   */
  wasCold: boolean;
}

/**
 * Singleton bus that coordinates navigation start (from ClientProviders /
 * usePathname change) with the ready signal (from useIncrementalRender or
 * LoadingPage unmount). One pending nav at a time per pathname: the
 * latest navigation supersedes the previous one, and ready signals for a
 * pathname that no longer matches the pending nav are dropped silently.
 */
class RouteInteractiveTracker {
  private pending: PendingNav | null = null;
  private isFirstNav = true;
  /** Outlives finalize(): a page's own late-arriving metric reads it. */
  private latest: RouteNavigation | null = null;

  start(pathname: string, serverMs: number | null): void {
    // Supersede any in-flight nav (user navigated again before ready).
    if (this.pending) {
      this.clearTimer();
      this.pending = null;
    }

    const startedAt = Date.now();
    const capTimer = setTimeout(() => {
      this.finalize("cap", "unknown");
    }, READY_CAP_MS);

    // BRE-4346 review: capture was_cold here, not in finalize(). If the
    // first navigation gets superseded before it finalises, the second
    // nav must NOT also report was_cold:true.
    const wasCold = this.isFirstNav;
    this.isFirstNav = false;
    this.latest = { pathname, startedAt, wasCold };

    this.pending = {
      pathname,
      startedAt,
      serverMs,
      capTimer,
      wasCold,
    };
  }

  markReady(
    pathname: string,
    signal: RouteInteractiveReadySignal,
    dataSource: RouteInteractiveDataSource = "unknown",
  ): void {
    if (!this.pending) return;
    if (this.pending.pathname !== pathname) return;
    this.finalize(signal, dataSource);
  }

  getLatest(): RouteNavigation | null {
    return this.latest;
  }

  reset(): void {
    this.clearTimer();
    this.pending = null;
    this.isFirstNav = true;
    this.latest = null;
  }

  private clearTimer(): void {
    if (this.pending?.capTimer) {
      clearTimeout(this.pending.capTimer);
    }
  }

  private finalize(
    signal: RouteInteractiveReadySignal,
    dataSource: RouteInteractiveDataSource,
  ): void {
    const pending = this.pending;
    if (!pending) return;
    this.clearTimer();
    this.pending = null;

    const wasCold = pending.wasCold;

    const rawTotal = Date.now() - pending.startedAt;
    const total = clampDurationMs(rawTotal);
    if (total === null) return; // drop garbage (clock skew, backgrounded tab)

    // Kill switch: skip if flag disabled OR unevaluated. Fail closed.
    let killSwitchEnabled = false;
    try {
      const flag = posthog.isFeatureEnabled(KILL_SWITCH_FLAG);
      killSwitchEnabled = flag === true;
    } catch {
      killSwitchEnabled = false;
    }
    if (!killSwitchEnabled) return;

    const severity = bandSeverity(total);

    // Per-severity sampling (slow/very_slow/critical default to 1.0).
    const rates = readSampleRates();
    const rate = rates[severity];
    if (Math.random() >= rate) return;

    const serverMs = pending.serverMs;
    const clientMs = serverMs !== null ? Math.max(0, total - serverMs) : total;
    const wasCapped = signal === "cap";

    try {
      captureProductClientEvent(ANALYTICS_EVENTS.ROUTE_INTERACTIVE, {
        route: pending.pathname,
        total_ms: total,
        client_ms: Math.round(clientMs),
        server_ms: serverMs,
        data_source: dataSource,
        was_cold: wasCold,
        was_capped: wasCapped,
        severity,
        ready_signal: signal,
      });
    } catch {
      // capture must never throw
    }
  }
}

const tracker = new RouteInteractiveTracker();

/** Public API for external signals (incremental-render hook, LoadingPage). */
export function markRouteInteractiveReady(
  pathname: string,
  signal: RouteInteractiveReadySignal,
  dataSource?: RouteInteractiveDataSource,
): void {
  tracker.markReady(pathname, signal, dataSource);
}

/** BRE-7925: the latest navigation, for page-level load metrics that land
 *  after `route.interactive` has already finalized. */
export function getLatestRouteNavigation(): RouteNavigation | null {
  return tracker.getLatest();
}

interface UseRouteInteractiveTrackingArgs {
  /** Server-side prefetch duration in ms, if known for this navigation. */
  serverMs?: number | null;
}

/**
 * Mount once near the top of the authenticated tree. On every pathname
 * change starts a new navigation timer that will be finalised by either
 * the incremental-render hook, LoadingPage unmount, or the 30s cap.
 */
export function useRouteInteractiveTracking({
  serverMs = null,
}: UseRouteInteractiveTrackingArgs = {}): void {
  const pathname = usePathname();
  const initialServerMsRef = useRef<number | null>(serverMs);

  useEffect(() => {
    if (!pathname) return;
    // serverMs is only meaningful on the very first nav (the layout
    // prefetch was for that page). Subsequent client navigations use null.
    const sm = initialServerMsRef.current;
    initialServerMsRef.current = null;
    tracker.start(pathname, sm);
  }, [pathname]);
}
